import { Check, Info } from 'lucide-react';
import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/*
 * OnboardingProgress — horizontal step indicator for `pages/admin/onboarding.tsx`.
 *
 * Extracted from the page during INTEGRATION_FINALIZE_PLAN chunk 46 so
 * the page shell can stay focused on flow logic. We keep a bespoke
 * horizontal indicator (rather than the booking-funnel `<Stepper>`,
 * which is a vertical accordion) because the onboarding UX renders ONE
 * step at a time below the progress bar, not a stack of cards.
 */

export interface OnboardingProgressStep {
  /** Stable id used to derive index against `current`. */
  id: string;
  /** Short label (e.g. "Profile"). Hidden on small viewports. */
  label: string;
}

/** The four screens of signup, in order. */
export const ONBOARDING_PROGRESS_STEPS = [
  { id: 'business', labelKey: 'onboarding:newFlow.stepLabels.business', fallback: 'Business' },
  { id: 'profile', labelKey: 'onboarding:newFlow.stepLabels.profile', fallback: 'Profile' },
  { id: 'service', labelKey: 'onboarding:newFlow.stepLabels.service', fallback: 'Service' },
  { id: 'launch', labelKey: 'onboarding:newFlow.stepLabels.launch', fallback: 'Launch' },
] as const;

interface OnboardingProgressProps {
  steps: OnboardingProgressStep[];
  current: string;
}

export function OnboardingProgress({ steps, current }: OnboardingProgressProps) {
  const currentIndex = steps.findIndex((s) => s.id === current);

  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => {
        const isActive = index === currentIndex;
        const isCompleted = index < currentIndex;
        const isLast = index === steps.length - 1;

        return (
          <div key={step.id} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all',
                  isCompleted && 'bg-brand-600 text-white',
                  isActive && 'bg-brand-600 text-white ring-4 ring-brand-100',
                  !isActive && !isCompleted && 'bg-surface border-2 border-line text-soft',
                )}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : index + 1}
              </div>
              <span
                className={cn(
                  'hidden sm:block text-xs font-medium mt-1.5 whitespace-nowrap',
                  isActive && 'text-brand-600',
                  isCompleted && 'text-brand-600',
                  !isActive && !isCompleted && 'text-soft',
                )}
              >
                {step.label}
              </span>
            </div>
            {!isLast && (
              <div
                className={cn(
                  'h-0.5 w-10 sm:w-16 mx-1 sm:mx-2 -mt-4 sm:mt-0 sm:-mb-5',
                  index < currentIndex ? 'bg-brand-600' : 'bg-line',
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * OnboardingHint — info callout shown beside form fields in the
 * onboarding flow. Brand-50 surface, brand-600 icon, brand-800 body.
 */
export function OnboardingHint({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 p-3 sm:p-4 bg-brand-50 border border-brand-200 rounded-xl">
      <Info className="h-4 w-4 text-brand-600 shrink-0 mt-0.5" />
      <p className="text-sm leading-relaxed text-brand-800">{children}</p>
    </div>
  );
}
